import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line, ComposedChart, ReferenceLine } from 'recharts';
import { mergeStockByTimestamp, mergePremiumByTimestamp, STOCK_COLOR, STOCK_DASH_STRING, formatStockPrice, formatPremiumPct } from '../utils/stockOverlay';

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        const date = new Date(label);
        const totalPosts = payload[0]?.payload?.totalPosts || 0;
        const volumeRatio = payload[0]?.payload?.weeklyVolume || 0;
        const stockPrice = payload.find(entry => entry.dataKey === 'stockPrice')?.value;
        const premiumPct = payload.find(entry => entry.dataKey === 'premiumPct')?.value;
        const competitorEntries = payload.filter(entry =>
            String(entry.dataKey).startsWith('competitorScore') && entry.value != null
        );

        // Calculate NPS from sentiment percentages (0-100 scale)
        const data = payload[0]?.payload || {};
        const promoters = (data.percentVeryPositive || 0) + ((data.percentModeratePositive || 0) * 0.5);
        const detractors = (data.percentVeryNegative || 0) + (data.percentModerateNegative || 0) + ((data.percentNeutral || 0) * 0.33);
        const npsScore = Math.round(((promoters - detractors) + 100) / 2);

        // Filter and order sentiment entries (negative to positive, matching stack order)
        // Exclude cumulative keys (used only for dot positioning) and other non-sentiment keys
        const sentimentEntries = payload
            .filter(entry => !['percentMeetingCriteria', 'weeklyVolume', 'stockPrice', 'premiumPct', 'sentimentScore', 'cumVeryNegative', 'cumModerateNegative', 'cumNeutral', 'cumModeratePositive', 'cumVeryPositive'].includes(entry.dataKey)
                && !String(entry.dataKey).startsWith('competitorScore'))
            .sort((a, b) => {
                const order = ['percentVeryPositive', 'percentModeratePositive', 'percentNeutral', 'percentModerateNegative', 'percentVeryNegative'];
                return order.indexOf(a.dataKey) - order.indexOf(b.dataKey);
            });

        // Section label style
        const sectionLabel = {
            fontSize: '9px',
            fontWeight: 600,
            color: '#999',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '4px'
        };

        return (
            <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.96)',
                padding: '10px 12px',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                minWidth: '160px',
                maxWidth: '200px'
            }}>
                {/* Primary: Date */}
                <div style={{
                    fontSize: '13px',
                    fontWeight: 700,
                    color: '#222',
                    marginBottom: '2px'
                }}>
                    {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>

                {/* Secondary: NPS Score */}
                <div style={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: npsScore >= 50 ? '#16a34a' : '#dc2626',
                    marginBottom: '4px'
                }}>
                    {npsScore}
                </div>

                {/* Tertiary: Context metrics - more subdued */}
                <div style={{
                    fontSize: '10px',
                    color: '#999',
                    marginBottom: '6px'
                }}>
                    {totalPosts.toLocaleString()} posts • {volumeRatio.toFixed(0)}% vol
                </div>

                {/* Stock price (when overlaid) */}
                {stockPrice != null && (
                    <div style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#FF6B00',
                        marginBottom: '6px'
                    }}>
                        Stock: ${Number(stockPrice).toFixed(2)}
                    </div>
                )}

                {/* Premium vs benchmark (when overlaid) */}
                {premiumPct != null && (
                    <div style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#FF6B00',
                        marginBottom: '6px'
                    }}>
                        Premium: {formatPremiumPct(premiumPct)}
                    </div>
                )}

                {/* Competitor comparison scores (when comparing) */}
                {competitorEntries.length > 0 && (
                <>
                <div style={{
                    borderTop: '1px solid #f0f0f0',
                    margin: '0 -12px 6px',
                    padding: '0 12px'
                }} />
                <div style={sectionLabel}>Competitors</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', marginBottom: '6px' }}>
                    {competitorEntries.map((entry, index) => (
                        <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            fontSize: '11px',
                            lineHeight: 1.3
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{
                                    width: '6px',
                                    height: '6px',
                                    borderRadius: '1px',
                                    backgroundColor: entry.color,
                                    flexShrink: 0
                                }} />
                                <span style={{ color: '#333' }}>{entry.name}</span>
                            </div>
                            <span style={{
                                fontWeight: 600,
                                color: '#222',
                                minWidth: '32px',
                                textAlign: 'right',
                                fontVariantNumeric: 'tabular-nums'
                            }}>
                                {Math.round(entry.value)}
                            </span>
                        </div>
                    ))}
                </div>
                </>
                )}

                {/* Sentiment Mix section — only in the distribution view, where
                    the stacked areas feed the tooltip payload. */}
                {sentimentEntries.length > 0 && (
                <>
                {/* Divider - softer */}
                <div style={{
                    borderTop: '1px solid #f0f0f0',
                    margin: '0 -12px 6px',
                    padding: '0 12px'
                }} />

                {/* Section: Sentiment Mix */}
                <div style={sectionLabel}>Sentiment Mix</div>

                {/* Sentiment rows - tighter spacing */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                    {sentimentEntries.map((entry, index) => (
                        <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            fontSize: '11px',
                            lineHeight: 1.3
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                {/* Color bullet - slightly smaller */}
                                <span style={{
                                    width: '6px',
                                    height: '6px',
                                    borderRadius: '1px',
                                    backgroundColor: entry.color,
                                    flexShrink: 0
                                }} />
                                {/* Label in darker neutral for crisp readability */}
                                <span style={{ color: '#333' }}>{entry.name}</span>
                            </div>
                            {/* Right-aligned percentage with fixed width for column alignment */}
                            <span style={{
                                fontWeight: 600,
                                color: '#222',
                                minWidth: '32px',
                                textAlign: 'right',
                                fontVariantNumeric: 'tabular-nums'
                            }}>
                                {entry.value.toFixed(0)}%
                            </span>
                        </div>
                    ))}
                </div>
                </>
                )}
            </div>
        );
    }
    return null;
};

// Line colors for competitor score series (same palette as NPSComparisonChart)
const COMPETITOR_COLORS = ['#4bc0c0', '#8a2be2', '#86002e', '#ff6347', '#32cd32', '#ff8c00'];

const PricingChart = ({ data, showTwoYears, defaultLinearScale = false, onPointClick, stockSeries = null, stockTicker = null, premiumData = null, benchLabel = null, competitorSeries = null, mainLineName = null }) => {
    const [showWhiteLine, setShowWhiteLine] = React.useState(false);
    // View: 'distribution' (stacked green→red areas) or 'line' (single sentiment
    // score time series).
    const [viewMode, setViewMode] = React.useState('distribution');
    const isLine = viewMode === 'line';

    const hasCompetitors = Array.isArray(competitorSeries) && competitorSeries.length > 0;

    // Competitor comparison is line-vs-line, so default into the score-line
    // view when competitor series arrive (user can still toggle back).
    React.useEffect(() => {
        if (hasCompetitors) setViewMode('line');
    }, [hasCompetitors]);

    if (!data || data.length === 0) {
        return <div>No data available</div>;
    }

    const hasStock = Array.isArray(stockSeries) && stockSeries.length > 0;
    const hasPremium = !hasStock && Array.isArray(premiumData) && premiumData.length > 0;
    const stockLabel = `Stock Price${stockTicker ? ` (${stockTicker})` : ''}`;
    const premiumLabel = `${stockTicker ? `${stockTicker} ` : ''}vs ${benchLabel || 'Benchmark'}`;

    // Calculate weekly volume as ratio to NPS volume (only if not already provided)
    // Also calculate cumulative values for dot positioning
    const enrichedData = data.map((point, index) => {
        const currentVolume = point.totalPosts / 4; // Current type weekly approximation
        const npsVolume = point.npsVolume / 4; // NPS weekly approximation

        // Only calculate ratio if weeklyVolume is not already provided (for backward compatibility)
        const volumeRatio = point.weeklyVolume !== undefined ?
            point.weeklyVolume : // Use pre-calculated smooth value if available
            (npsVolume > 0 ? Math.min((currentVolume / npsVolume) * 100, 80) : 0);

        // Calculate cumulative values for dot positioning (matching stack order: green bottom, red top)
        const cumVeryPositive = point.percentVeryPositive || 0;
        const cumModeratePositive = cumVeryPositive + (point.percentModeratePositive || 0);
        const cumNeutral = cumModeratePositive + (point.percentNeutral || 0);
        const cumModerateNegative = cumNeutral + (point.percentModerateNegative || 0);
        const cumVeryNegative = cumModerateNegative + (point.percentVeryNegative || 0);

        // Single sentiment score (0-100) for the line view — same NPS formula
        // used in the tooltip and SentimentAnalysisChart.
        const promoters = (point.percentVeryPositive || 0) + ((point.percentModeratePositive || 0) * 0.5);
        const detractors = (point.percentVeryNegative || 0) + (point.percentModerateNegative || 0) + ((point.percentNeutral || 0) * 0.33);
        const sentimentScore = Math.round(((promoters - detractors) + 100) / 2);

        return {
            ...point,
            weeklyVolume: volumeRatio,
            rawCurrentVolume: currentVolume,
            rawNpsVolume: npsVolume,
            sentimentScore,
            // Cumulative values for dot lines
            cumVeryNegative,
            cumModerateNegative,
            cumNeutral,
            cumModeratePositive,
            cumVeryPositive
        };
    });

    console.log('24-month debug:', {
        dataLength: enrichedData.length,
        firstPoint: enrichedData[0],
        hasNaN: enrichedData.some(p => isNaN(p.weeklyVolume))
    });

    // Pick x-axis label granularity from the actual span of the data. A short
    // window (e.g. the 4-week view, ~28 daily points) sits inside one or two
    // months, so a "MMM YYYY" label collapses to the same string on every tick.
    // In that case include the day; otherwise keep the month/year label.
    const firstTimestamp = enrichedData[0]?.timestamp;
    const lastTimestamp = enrichedData[enrichedData.length - 1]?.timestamp;
    const spanDays = (firstTimestamp && lastTimestamp)
        ? (lastTimestamp - firstTimestamp) / (1000 * 60 * 60 * 24)
        : 0;
    const isShortRange = spanDays > 0 && spanDays <= 70;
    const formatAxisTick = (timestamp) => {
        const date = new Date(timestamp);
        if (isShortRange) {
            // Daily/short window: show month + day (e.g. "Jun 5").
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
        return `${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`;
    };

    // Merge the stock price or premium onto each bucket (by timestamp) when
    // overlaying.
    let chartRows = hasStock
        ? mergeStockByTimestamp(enrichedData, stockSeries)
        : hasPremium
            ? mergePremiumByTimestamp(enrichedData, premiumData)
            : enrichedData;

    // Merge competitor sentiment scores onto each bucket (by timestamp). Both
    // series come from the same RPC at the same frequency, so buckets align;
    // missing days stay null and the lines connect across them.
    if (hasCompetitors) {
        const scoreMaps = competitorSeries.map(s => new Map(s.points.map(p => [p.timestamp, p.score])));
        chartRows = chartRows.map(row => {
            const withScores = { ...row };
            scoreMaps.forEach((m, i) => {
                withScores[`competitorScore${i}`] = m.has(row.timestamp) ? m.get(row.timestamp) : null;
            });
            return withScores;
        });
    }

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '10px 0', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                    onClick={() => setViewMode(isLine ? 'distribution' : 'line')}
                    style={{
                        padding: '5px 14px',
                        backgroundColor: isLine ? '#1042c1' : '#f5f5f5',
                        color: isLine ? '#fff' : '#555',
                        border: isLine ? '1px solid #1042c1' : '1px solid #ddd',
                        borderRadius: '20px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s ease',
                    }}
                >
                    <span style={{
                        width: '7px',
                        height: '7px',
                        borderRadius: '50%',
                        backgroundColor: isLine ? '#7dd3fc' : '#aaa',
                        transition: 'background-color 0.2s ease',
                    }} />
                    {isLine ? 'Distribution View' : 'Score Line View'}
                </button>
                <button
                    onClick={() => setShowWhiteLine(!showWhiteLine)}
                    style={{
                        padding: '5px 14px',
                        backgroundColor: showWhiteLine ? '#1042c1' : '#f5f5f5',
                        color: showWhiteLine ? '#fff' : '#555',
                        border: showWhiteLine ? '1px solid #1042c1' : '1px solid #ddd',
                        borderRadius: '20px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                        if (!showWhiteLine) {
                            e.currentTarget.style.backgroundColor = '#eee';
                            e.currentTarget.style.borderColor = '#ccc';
                        } else {
                            e.currentTarget.style.backgroundColor = '#0d38a3';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!showWhiteLine) {
                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                            e.currentTarget.style.borderColor = '#ddd';
                        } else {
                            e.currentTarget.style.backgroundColor = '#1042c1';
                        }
                    }}
                >
                    <span style={{
                        width: '7px',
                        height: '7px',
                        borderRadius: '50%',
                        backgroundColor: showWhiteLine ? '#7dd3fc' : '#aaa',
                        transition: 'background-color 0.2s ease',
                    }} />
                    {showWhiteLine ? 'Hide Volume' : 'Show Volume'}
                </button>
            </div>
            <div style={{ flex: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={chartRows}
                        margin={{
                            top: 10,
                            right: 30,
                            left: 0,
                            bottom: 20,
                        }}
                        style={{ position: 'relative', cursor: onPointClick ? 'pointer' : 'default' }}
                        onClick={(e) => {
                            if (onPointClick && e && e.activePayload && e.activePayload.length > 0) {
                                const pointData = e.activePayload[0].payload;
                                onPointClick({
                                    timestamp: pointData.timestamp,
                                    data: pointData
                                });
                            }
                        }}
                    >
                        <defs>
                            <clipPath id="chartClip">
                                <rect x="0" y="0" width="100%" height="100%" />
                            </clipPath>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                            dataKey="timestamp"
                            tickFormatter={formatAxisTick}
                            ticks={enrichedData.reduce((acc, point, i) => {
                                const totalPoints = enrichedData.length;
                                const numTicks = showTwoYears ? 8 : (isShortRange ? 7 : 4);
                                const step = Math.floor(totalPoints / (numTicks - 1));
                                
                                // Add evenly spaced ticks
                                if (i % step === 0) {
                                    acc.push(point.timestamp);
                                }
                                
                                // Add the last point only if it's not too close to the previous tick
                                if (i === totalPoints - 1) {
                                    const lastAddedIndex = acc.length > 0 ? enrichedData.findIndex(p => p.timestamp === acc[acc.length - 1]) : -1;
                                    if (lastAddedIndex === -1 || (i - lastAddedIndex) >= step / 2) {
                                        acc.push(point.timestamp);
                                    }
                                }
                                
                                return acc;
                            }, [])}
                            angle={-45}
                            textAnchor="end"
                            height={70}
                            dy={10}
                        />
                        <YAxis 
                            yAxisId="left"
                            orientation="left"
                            domain={[0, 100]}
                            tickFormatter={(value) => `${value}%`}
                            ticks={[0, 20, 40, 60, 80, 100]}
                            label={{
                                value: isLine ? 'Sentiment Score' : 'Distribution of Sentiment',
                                angle: 90,
                                position: 'insideLeft',
                                style: { textAnchor: 'middle', fontSize: '12px', fill: '#ccc' }
                            }}
                        />
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            scale="linear"
                            domain={[0, 100]}
                            tickFormatter={(value) => `${value.toFixed(0)}%`}
                            tick={{ fontSize: 11, fill: '#ccc' }}
                            axisLine={{ stroke: '#ddd', strokeWidth: 1 }}
                            tickLine={{ stroke: '#ddd', strokeWidth: 1 }}
                            label={{
                                value: '% Posts Mentioning Topic',
                                angle: 90,
                                position: 'insideRight',
                                style: { textAnchor: 'middle', fontSize: '12px', fill: '#ccc' }
                            }}
                        />
                        {hasStock && (
                            <YAxis
                                yAxisId="price"
                                orientation="right"
                                domain={['auto', 'auto']}
                                tickFormatter={formatStockPrice}
                                tick={{ fontSize: 11, fill: STOCK_COLOR }}
                                axisLine={{ stroke: STOCK_COLOR, strokeWidth: 1 }}
                                tickLine={{ stroke: STOCK_COLOR, strokeWidth: 1 }}
                                width={64}
                                label={{
                                    value: 'Stock Price ($)',
                                    angle: 90,
                                    position: 'insideRight',
                                    style: { textAnchor: 'middle', fontSize: '12px', fill: STOCK_COLOR }
                                }}
                            />
                        )}
                        {hasPremium && (
                            <YAxis
                                yAxisId="premium"
                                orientation="right"
                                domain={([dataMin, dataMax]) => [Math.min(0, dataMin), Math.max(0, dataMax)]}
                                tickFormatter={(value) => `${Math.round(value)}%`}
                                tick={{ fontSize: 11, fill: STOCK_COLOR }}
                                axisLine={{ stroke: STOCK_COLOR, strokeWidth: 1 }}
                                tickLine={{ stroke: STOCK_COLOR, strokeWidth: 1 }}
                                width={64}
                                label={{
                                    value: 'Premium (%)',
                                    angle: 90,
                                    position: 'insideRight',
                                    style: { textAnchor: 'middle', fontSize: '12px', fill: STOCK_COLOR }
                                }}
                            />
                        )}
                        <Tooltip
                            content={<CustomTooltip />}
                            wrapperStyle={{ zIndex: 1000 }}
                            cursor={{ stroke: '#666', strokeWidth: 1 }}
                        />
                        <Legend
                            verticalAlign="top"
                            wrapperStyle={{
                                paddingBottom: '15px'
                            }}
                        />
                        

                        {/* Distribution view: stacked green→red sentiment areas */}
                        {!isLine && (
                        <>
                        <Area
                            yAxisId="left"
                            type="monotone"
                            dataKey="percentVeryPositive"
                            name="Exceptional"
                            stackId="1"
                            stroke="#669756"
                            fill="#98D586"
                            fillOpacity={1}
                            strokeOpacity={1}
                            strokeWidth={1}
                            style={{ mixBlendMode: 'normal' }}
                            isAnimationActive={false}
                            activeDot={false}
                        />
                        <Area
                            yAxisId="left"
                            type="monotone"
                            dataKey="percentModeratePositive"
                            name="Good"
                            stackId="1"
                            stroke="#849782"
                            fill="#BED5BA"
                            fillOpacity={1}
                            strokeOpacity={1}
                            strokeWidth={1}
                            style={{ mixBlendMode: 'normal' }}
                            isAnimationActive={false}
                            activeDot={false}
                        />
                        <Area
                            yAxisId="left"
                            type="monotone"
                            dataKey="percentNeutral"
                            name="Average"
                            stackId="1"
                            stroke="#7D7D7D"
                            fill="#B7B7B7"
                            fillOpacity={1}
                            strokeOpacity={1}
                            strokeWidth={1}
                            style={{ mixBlendMode: 'normal' }}
                            isAnimationActive={false}
                            activeDot={false}
                        />
                        <Area
                            yAxisId="left"
                            type="monotone"
                            dataKey="percentModerateNegative"
                            name="Poor"
                            stackId="1"
                            stroke="#B68180"
                            fill="#F6BBBA"
                            fillOpacity={1}
                            strokeOpacity={1}
                            strokeWidth={1}
                            style={{ mixBlendMode: 'normal' }}
                            isAnimationActive={false}
                            activeDot={false}
                        />
                        <Area
                            yAxisId="left"
                            type="monotone"
                            dataKey="percentVeryNegative"
                            name="Very Bad"
                            stackId="1"
                            stroke="#B25147"
                            fill="#F2857B"
                            fillOpacity={1}
                            strokeOpacity={1}
                            strokeWidth={1}
                            style={{ mixBlendMode: 'normal' }}
                            isAnimationActive={false}
                            activeDot={false}
                        />
                        <Area
                            yAxisId="left"
                            type="monotone"
                            dataKey="% of Total Posts"
                            name="% of Total Posts Mentioning Topic"
                            stackId="1"
                            stroke="#aaaaaa"
                            fill="#cccccc"
                            fillOpacity={1}
                            strokeOpacity={1}
                            strokeWidth={1}
                            style={{ mixBlendMode: 'normal' }}
                            activeDot={false}
                        />
                        
                        {/* Invisible lines for activeDot positioning - rendered on top of all areas */}
                        <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="cumVeryPositive"
                            stroke="transparent"
                            strokeWidth={0}
                            dot={false}
                            activeDot={{ r: 3, stroke: '#669756', strokeWidth: 1.5, fill: '#98D586' }}
                            legendType="none"
                            isAnimationActive={false}
                        />
                        <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="cumModeratePositive"
                            stroke="transparent"
                            strokeWidth={0}
                            dot={false}
                            activeDot={{ r: 3, stroke: '#849782', strokeWidth: 1.5, fill: '#BED5BA' }}
                            legendType="none"
                            isAnimationActive={false}
                        />
                        <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="cumNeutral"
                            stroke="transparent"
                            strokeWidth={0}
                            dot={false}
                            activeDot={{ r: 3, stroke: '#7D7D7D', strokeWidth: 1.5, fill: '#B7B7B7' }}
                            legendType="none"
                            isAnimationActive={false}
                        />
                        <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="cumModerateNegative"
                            stroke="transparent"
                            strokeWidth={0}
                            dot={false}
                            activeDot={{ r: 3, stroke: '#B68180', strokeWidth: 1.5, fill: '#F6BBBA' }}
                            legendType="none"
                            isAnimationActive={false}
                        />
                        <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="cumVeryNegative"
                            stroke="transparent"
                            strokeWidth={0}
                            dot={false}
                            activeDot={{ r: 3, stroke: '#B25147', strokeWidth: 1.5, fill: '#F2857B' }}
                            legendType="none"
                            isAnimationActive={false}
                        />
                        </>
                        )}

                        {/* Score line view: single sentiment-score time series */}
                        {isLine && (
                            <Line
                                yAxisId="left"
                                type="monotone"
                                dataKey="sentimentScore"
                                name={mainLineName || 'Sentiment Score'}
                                stroke="#1042c1"
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ r: 4, stroke: '#fff', strokeWidth: 1.5, fill: '#1042c1' }}
                                isAnimationActive={false}
                            />
                        )}

                        {/* Competitor sentiment-score lines (same 0-100 left axis) */}
                        {hasCompetitors && competitorSeries.map((series, i) => (
                            <Line
                                key={series.id || series.name}
                                yAxisId="left"
                                type="monotone"
                                dataKey={`competitorScore${i}`}
                                name={series.name}
                                stroke={COMPETITOR_COLORS[i % COMPETITOR_COLORS.length]}
                                strokeWidth={2}
                                dot={false}
                                connectNulls
                                activeDot={{ r: 4, stroke: '#fff', strokeWidth: 1.5, fill: COMPETITOR_COLORS[i % COMPETITOR_COLORS.length] }}
                                isAnimationActive={false}
                            />
                        ))}

                        {/* Weekly Post Count Line - faint white line - conditionally rendered */}
                        {showWhiteLine && (
                            <Line
                                yAxisId="right"
                                type="basis"
                                dataKey="weeklyVolume"
                                name="% of Posts Mentioning Topic"
                                stroke="#FFFFFF"
                                legendType="none"
                                strokeWidth={3}
                                strokeOpacity={0.9}
                                dot={false}
                                activeDot={{ r: 4, stroke: '#333', strokeWidth: 1.5, fill: '#fff' }}
                                isAnimationActive={false}
                            />
                        )}
                        
                        {/* Stock price overlay line */}
                        {hasStock && (
                            <Line
                                yAxisId="price"
                                type="monotone"
                                dataKey="stockPrice"
                                name={stockLabel}
                                stroke={STOCK_COLOR}
                                strokeWidth={2}
                                strokeDasharray={STOCK_DASH_STRING}
                                dot={false}
                                connectNulls
                                activeDot={{ r: 4, stroke: '#fff', strokeWidth: 1.5, fill: STOCK_COLOR }}
                                isAnimationActive={false}
                            />
                        )}

                        {/* Premium overlay: zero baseline + the premium % line */}
                        {hasPremium && (
                            <ReferenceLine yAxisId="premium" y={0} stroke="#bbb" strokeDasharray="3 3" />
                        )}
                        {hasPremium && (
                            <Line
                                yAxisId="premium"
                                type="monotone"
                                dataKey="premiumPct"
                                name={premiumLabel}
                                stroke={STOCK_COLOR}
                                strokeWidth={2}
                                strokeDasharray={STOCK_DASH_STRING}
                                dot={false}
                                connectNulls
                                activeDot={{ r: 4, stroke: '#fff', strokeWidth: 1.5, fill: STOCK_COLOR }}
                                isAnimationActive={false}
                            />
                        )}

                        {/* Horizontal reference lines - moved to front */}
                        {/*<ReferenceLine yAxisId="left" y={20} stroke="#000000" strokeDasharray="4 6" strokeOpacity={0.9} strokeWidth={0.3}/>*/}
                        {/*<ReferenceLine yAxisId="left" y={40} stroke="#000000" strokeDasharray="4 6" strokeOpacity={0.9} strokeWidth={0.3}/>*/}
                        {/*<ReferenceLine yAxisId="left" y={60} stroke="#000000" strokeDasharray="4 6" strokeOpacity={0.9} strokeWidth={0.3}/>*/}
                        {/*<ReferenceLine yAxisId="left" y={80} stroke="#000000" strokeDasharray="4 6" strokeOpacity={0.9} strokeWidth={0.3}/>*/}
                        
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
            
            {/* Summary metrics now rendered in parent SentimentAnalysisChart component */}
        </div>
    );
};

export default PricingChart; 